/**
 * Spec 040: a staff member's console as the tasker draws it — a small terminal screen fed with
 * what the PC's console prints: text, the cursor's moves, erasing, colours, the full screen of
 * Claude Code. It answers nothing: the console's own queries are answered on the PC. No page and
 * no import here, so the tests feed it what a console prints.
 */

export interface Style {
  fg: string | null;
  bg: string | null;
  bold: boolean;
  dim: boolean;
  italic: boolean;
  underline: boolean;
  inverse: boolean;
}

/** A stretch of one line in one style. */
export interface Run {
  text: string;
  style: Style;
}

interface Cell {
  ch: string;
  style: Style;
}

export const PLAIN: Style = { fg: null, bg: null, bold: false, dim: false, italic: false, underline: false, inverse: false };

const BASIC = ["#1e1e1e", "#cd3131", "#0dbc79", "#e5e510", "#2472c8", "#bc3fbc", "#11a8cd", "#e5e5e5"];
const BRIGHT = ["#666666", "#f14c4c", "#23d18b", "#f5f543", "#3b8eea", "#d670d6", "#29b8db", "#ffffff"];
const PENDING_MAX = 4096; // an unfinished sequence longer than this is dropped, never waited for

export function color256(n: number): string {
  if (n < 8) return BASIC[n];
  if (n < 16) return BRIGHT[n - 8];
  if (n < 232) {
    const i = n - 16;
    const level = (x: number) => (x ? 55 + x * 40 : 0);
    return `rgb(${level(Math.floor(i / 36))},${level(Math.floor((i % 36) / 6))},${level(i % 6)})`;
  }
  const grey = 8 + (n - 232) * 10;
  return `rgb(${grey},${grey},${grey})`;
}

export class Screen {
  cols: number;
  rows: number;
  /** The cursor on the screen: column and row from 0. */
  x = 0;
  y = 0;
  private lines: Cell[][] = [];
  private main: Cell[][] | null = null; // the main screen while the alternate one is shown
  private saved = { x: 0, y: 0 };
  private style: Style = PLAIN;
  private pending = "";
  private readonly keep: number;

  /** `keep`: lines of scrollback over the screen. */
  constructor(cols = 120, rows = 30, keep = 1000) {
    this.cols = Math.max(1, cols);
    this.rows = Math.max(1, rows);
    this.keep = keep;
    for (let i = 0; i < this.rows; i++) this.lines.push(this.blank());
  }

  private blank(): Cell[] {
    const line: Cell[] = [];
    for (let i = 0; i < this.cols; i++) line.push({ ch: " ", style: PLAIN });
    return line;
  }

  private top(): number {
    return this.lines.length - this.rows;
  }

  private line(y: number): Cell[] {
    return this.lines[this.top() + y];
  }

  /** The PC's console changed its size: the lines keep what fits. */
  resize(cols: number, rows: number): void {
    this.cols = Math.max(1, cols);
    this.rows = Math.max(1, rows);
    this.lines = this.lines.map((line) => {
      const out = line.slice(0, this.cols);
      while (out.length < this.cols) out.push({ ch: " ", style: PLAIN });
      return out;
    });
    while (this.lines.length < this.rows) this.lines.unshift(this.blank());
    this.x = Math.min(this.x, this.cols - 1);
    this.y = Math.min(this.y, this.rows - 1);
  }

  private scrollUp(): void {
    this.lines.push(this.blank());
    if (this.main) this.lines.shift(); // the alternate screen keeps no scrollback
    else if (this.lines.length > this.rows + this.keep) this.lines.splice(0, this.lines.length - this.rows - this.keep);
  }

  private newline(): void {
    if (this.y < this.rows - 1) this.y += 1;
    else this.scrollUp();
  }

  /** The screen's lines from row `y` go down by one: the bottom one is lost, a blank comes in. */
  private insertLine(y: number): void {
    const top = this.top();
    this.lines.splice(top + this.rows - 1, 1);
    this.lines.splice(top + y, 0, this.blank());
  }

  private put(ch: string): void {
    if (this.x >= this.cols) {
      this.x = 0;
      this.newline();
    }
    this.line(this.y)[this.x] = { ch, style: this.style };
    this.x += 1;
  }

  private erase(y: number, from: number, to: number): void {
    const line = this.line(y);
    for (let i = Math.max(0, from); i < Math.min(this.cols, to); i++) line[i] = { ch: " ", style: PLAIN };
  }

  /** What the console printed, in pieces as they come: a sequence cut between two waits for the next. */
  write(data: string): void {
    const s = this.pending + data;
    this.pending = "";
    let i = 0;
    while (i < s.length) {
      const c = s[i];
      if (c === "\x1b") {
        const next = this.escape(s, i);
        if (next < 0) {
          this.pending = s.slice(i).length > PENDING_MAX ? "" : s.slice(i);
          return;
        }
        i = next;
        continue;
      }
      const code = s.charCodeAt(i);
      if (c === "\r") this.x = 0;
      else if (c === "\n" || c === "\x0b" || c === "\x0c") this.newline();
      else if (c === "\b") this.x = Math.max(0, Math.min(this.x, this.cols) - 1);
      else if (c === "\t") this.x = Math.min(this.cols - 1, (Math.floor(this.x / 8) + 1) * 8);
      else if (code >= 0x20 && code !== 0x7f) {
        if (code >= 0xd800 && code <= 0xdbff && i + 1 < s.length) {
          this.put(s.slice(i, i + 2));
          i += 2;
          continue;
        }
        this.put(c);
      }
      i += 1;
    }
  }

  /** Where the sequence at `i` ends, or -1 while it is not whole yet. */
  private escape(s: string, i: number): number {
    const kind = s[i + 1];
    if (kind === undefined) return -1;
    if (kind === "[") {
      let j = i + 2;
      while (j < s.length && s.charCodeAt(j) >= 0x20 && s.charCodeAt(j) <= 0x3f) j++;
      if (j >= s.length) return -1;
      this.csi(s.slice(i + 2, j), s[j]);
      return j + 1;
    }
    if (kind === "]" || kind === "P" || kind === "_" || kind === "^") {
      // A title, a device control string: up to BEL or ST, nothing drawn.
      for (let j = i + 2; j < s.length; j++) {
        if (s[j] === "\x07") return j + 1;
        if (s[j] === "\x1b" && s[j + 1] === "\\") return j + 2;
        if (s[j] === "\x1b" && s[j + 1] === undefined) return -1;
      }
      return -1;
    }
    if (kind === "(" || kind === ")" || kind === "*" || kind === "+") return s[i + 2] === undefined ? -1 : i + 3;
    if (kind === "7") this.saved = { x: this.x, y: this.y };
    else if (kind === "8") ({ x: this.x, y: this.y } = this.saved);
    else if (kind === "D") this.newline();
    else if (kind === "E") {
      this.x = 0;
      this.newline();
    } else if (kind === "M") {
      if (this.y > 0) this.y -= 1;
      else this.insertLine(0);
    } else if (kind === "c") {
      this.style = PLAIN;
      this.x = 0;
      this.y = 0;
      for (let y = 0; y < this.rows; y++) this.erase(y, 0, this.cols);
    }
    return i + 2;
  }

  private csi(params: string, final: string): void {
    const priv = /^[?>=]/.test(params);
    const nums = params.replace(/^[?>=]/, "").replace(/:/g, ";").split(";").map((n) => (n === "" ? NaN : Number(n)));
    const n = (k: number, fallback = 1) => (Number.isFinite(nums[k]) && nums[k] > 0 ? nums[k] : fallback);
    if (priv) {
      if (final === "h" || final === "l") {
        for (const mode of nums) if (mode === 1049 || mode === 1047 || mode === 47) this.alternate(final === "h");
      }
      return;
    }
    switch (final) {
      case "A": this.y = Math.max(0, this.y - n(0)); break;
      case "B": this.y = Math.min(this.rows - 1, this.y + n(0)); break;
      case "C": this.x = Math.min(this.cols - 1, this.x + n(0)); break;
      case "D": this.x = Math.max(0, Math.min(this.x, this.cols) - n(0)); break;
      case "E": this.y = Math.min(this.rows - 1, this.y + n(0)); this.x = 0; break;
      case "F": this.y = Math.max(0, this.y - n(0)); this.x = 0; break;
      case "G": case "`": this.x = Math.min(this.cols - 1, n(0) - 1); break;
      case "d": this.y = Math.min(this.rows - 1, n(0) - 1); break;
      case "H": case "f":
        this.y = Math.min(this.rows - 1, n(0) - 1);
        this.x = Math.min(this.cols - 1, n(1) - 1);
        break;
      case "J": this.eraseDisplay(Number.isFinite(nums[0]) ? nums[0] : 0); break;
      case "K": {
        const mode = Number.isFinite(nums[0]) ? nums[0] : 0;
        if (mode === 0) this.erase(this.y, this.x, this.cols);
        else if (mode === 1) this.erase(this.y, 0, this.x + 1);
        else this.erase(this.y, 0, this.cols);
        break;
      }
      case "X": this.erase(this.y, this.x, this.x + n(0)); break;
      case "P": {
        const line = this.line(this.y);
        const count = Math.min(n(0), this.cols - this.x);
        line.splice(this.x, count);
        for (let k = 0; k < count; k++) line.push({ ch: " ", style: PLAIN });
        break;
      }
      case "@": {
        const line = this.line(this.y);
        const count = Math.min(n(0), this.cols - this.x);
        for (let k = 0; k < count; k++) line.splice(this.x, 0, { ch: " ", style: PLAIN });
        line.length = this.cols;
        break;
      }
      case "L": case "M": {
        const count = Math.min(n(0), this.rows - this.y);
        for (let k = 0; k < count; k++) {
          if (final === "L") this.insertLine(this.y);
          else {
            this.lines.splice(this.top() + this.y, 1);
            this.lines.push(this.blank());
          }
        }
        break;
      }
      case "S": for (let k = 0; k < n(0); k++) this.scrollUp(); break;
      case "T": for (let k = 0; k < n(0); k++) this.insertLine(0); break;
      case "m": this.sgr(nums); break;
      case "s": this.saved = { x: this.x, y: this.y }; break;
      case "u": ({ x: this.x, y: this.y } = this.saved); break;
      default: break; // queries, scroll regions, modes: not drawn
    }
  }

  private eraseDisplay(mode: number): void {
    if (mode === 0) {
      this.erase(this.y, this.x, this.cols);
      for (let y = this.y + 1; y < this.rows; y++) this.erase(y, 0, this.cols);
    } else if (mode === 1) {
      for (let y = 0; y < this.y; y++) this.erase(y, 0, this.cols);
      this.erase(this.y, 0, this.x + 1);
    } else {
      for (let y = 0; y < this.rows; y++) this.erase(y, 0, this.cols);
      if (mode === 3) this.lines.splice(0, this.top());
    }
  }

  private alternate(on: boolean): void {
    if (on && !this.main) {
      this.main = this.lines;
      this.lines = [];
      for (let i = 0; i < this.rows; i++) this.lines.push(this.blank());
      this.x = 0;
      this.y = 0;
    } else if (!on && this.main) {
      this.lines = this.main;
      this.main = null;
    }
  }

  private sgr(nums: number[]): void {
    const codes = nums.length ? nums : [0];
    let s: Style = { ...this.style };
    for (let i = 0; i < codes.length; i++) {
      const c = Number.isFinite(codes[i]) ? codes[i] : 0;
      if (c === 0) s = { ...PLAIN };
      else if (c === 1) s.bold = true;
      else if (c === 2) s.dim = true;
      else if (c === 3) s.italic = true;
      else if (c === 4) s.underline = true;
      else if (c === 7) s.inverse = true;
      else if (c === 22) { s.bold = false; s.dim = false; }
      else if (c === 23) s.italic = false;
      else if (c === 24) s.underline = false;
      else if (c === 27) s.inverse = false;
      else if (c >= 30 && c <= 37) s.fg = BASIC[c - 30];
      else if (c === 39) s.fg = null;
      else if (c >= 40 && c <= 47) s.bg = BASIC[c - 40];
      else if (c === 49) s.bg = null;
      else if (c >= 90 && c <= 97) s.fg = BRIGHT[c - 90];
      else if (c >= 100 && c <= 107) s.bg = BRIGHT[c - 100];
      else if (c === 38 || c === 48) {
        let color: string | null = null;
        if (codes[i + 1] === 5 && Number.isFinite(codes[i + 2])) {
          color = color256(codes[i + 2]);
          i += 2;
        } else if (codes[i + 1] === 2 && i + 4 < codes.length) {
          color = `rgb(${codes[i + 2] || 0},${codes[i + 3] || 0},${codes[i + 4] || 0})`;
          i += 4;
        } else i = codes.length;
        if (color) {
          if (c === 38) s.fg = color;
          else s.bg = color;
        }
      }
    }
    this.style = s;
  }

  /** The last `limit` lines — scrollback and the screen — as stretches of one style. */
  view(limit = 400): Run[][] {
    return this.lines.slice(-limit).map((line) => {
      let end = line.length;
      while (end > 0 && line[end - 1].ch === " " && line[end - 1].style === PLAIN) end--;
      const runs: Run[] = [];
      for (let i = 0; i < end; i++) {
        const cell = line[i];
        const last = runs[runs.length - 1];
        if (last && last.style === cell.style) last.text += cell.ch;
        else runs.push({ text: cell.ch, style: cell.style });
      }
      return runs;
    });
  }

  /** The lines as plain text, the screen's last; trailing blanks cut. */
  text(): string[] {
    return this.lines.map((line) => line.map((cell) => cell.ch).join("").replace(/\s+$/, ""));
  }
}
